import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button
} from '@mui/material';
import {
  Storage,
  Refresh,
  Home,
  Search,
  Clear,
  AccessTime,
  FolderOpen,
  Transform,
  InsertDriveFile
} from '@mui/icons-material';
import api from '../../services/api';
import { useEntityContext } from '../EntityContextSwitch';
import FileTreeItem, { formatFileSize, formatDate } from './FileTreeItem';

// eslint-disable-next-line max-lines-per-function
function GCSDetailView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bucketInfo, setBucketInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [ptlProcessing, setPtlProcessing] = useState(false);
  const [ptlMessage, setPtlMessage] = useState(null);
  const { currentEntity } = useEntityContext();

  const clientId = currentEntity?.id;

  const fetchBucketInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEntityGCSInfo(clientId);
      console.log('GCS bucket info received:', data);
      if (data.directoryStructure) {
        console.log('Directory structure:', JSON.stringify(data.directoryStructure, null, 2));
      }
      setBucketInfo(data);
    } catch (err) {
      console.error('Error fetching GCS info:', err);
      setError(err.message || 'Failed to load GCS information');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      fetchBucketInfo();
    }
  }, [clientId, fetchBucketInfo]);

  // Trigger PTL processing for all files in GCS
  const triggerPTLProcessing = async () => {
    setPtlProcessing(true);
    setPtlMessage(null);
    setError(null);

    try {
      const response = await api.triggerPTLProcessing(clientId);

      if (response.success) {
        setPtlMessage({
          type: 'success',
          text: `PTL processing started successfully. ${response.message || ''}`
        });

        // Clear success message after 5 seconds
        setTimeout(() => setPtlMessage(null), 5000);
      } else {
        setPtlMessage({
          type: 'error',
          text: response.error || 'Failed to trigger PTL processing'
        });
      }
    } catch (err) {
      console.error('Error triggering PTL:', err);
      setPtlMessage({
        type: 'error',
        text: err.message || 'Failed to trigger PTL processing'
      });
    } finally {
      setPtlProcessing(false);
    }
  };

  // Search function to find files in the directory structure
  const searchFiles = useCallback((structure, query, currentPath = '') => {
    const results = [];
    const lowerQuery = query.toLowerCase();

    const searchInStructure = (obj, path) => {
      Object.entries(obj).forEach(([name, data]) => {
        const fullPath = path ? `${path}/${name}` : name;

        if (data.type === 'file') {
          // Check if filename matches query
          if (name.toLowerCase().includes(lowerQuery)) {
            results.push({
              name,
              path: fullPath,
              size: data.size,
              lastModified: data.lastModified,
              metadata: data.metadata || {}
            });
          }
        } else if (data.type === 'directory' && data.files) {
          // Recursively search in subdirectories
          searchInStructure(data.files, fullPath);
        }
      });
    };

    searchInStructure(structure, currentPath);
    return results;
  }, []);

  // Handle search
  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() || !bucketInfo?.directoryStructure) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const results = searchFiles(bucketInfo.directoryStructure, searchQuery);
    setSearchResults(results);
  }, [searchQuery, bucketInfo, searchFiles]);

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  // Trigger search on query change (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!bucketInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No GCS bucket information available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storage color="primary" />
            {bucketInfo.bucketName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Process files through Privacy Tokenization Layer (PTL) to tokenize sensitive data before analysis">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={triggerPTLProcessing}
                  disabled={ptlProcessing}
                  startIcon={ptlProcessing ? <CircularProgress size={16} /> : <Transform />}
                  sx={{ mr: 1 }}
                >
                  {ptlProcessing ? 'Processing...' : 'Process with PTL'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh bucket information">
              <IconButton onClick={fetchBucketInfo} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* PTL Processing Message */}
        {ptlMessage && (
          <Alert
            severity={ptlMessage.type}
            sx={{ mb: 2 }}
            onClose={() => setPtlMessage(null)}
          >
            {ptlMessage.text}
          </Alert>
        )}

        {/* Bucket Summary */}
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Files
                </Typography>
                <Typography variant="h5">
                  {bucketInfo.fileCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Size
                </Typography>
                <Typography variant="h5">
                  {bucketInfo.totalSizeFormatted}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Storage Class
                </Typography>
                <Typography variant="h6">
                  {bucketInfo.storageClass}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Location
                </Typography>
                <Typography variant="h6">
                  {bucketInfo.location}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      {/* Search Bar */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search for files by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Search Results */}
      {isSearching && searchQuery && (
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            {searchResults.length > 0
              ? `Found ${searchResults.length} file${searchResults.length > 1 ? 's' : ''}`
              : 'No files found'
            }
          </Typography>
          {searchResults.length > 0 && (
            <Box sx={{ mt: 2, maxHeight: '400px', overflowY: 'auto' }}>
              {searchResults.map((file, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <InsertDriveFile color="action" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {file.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <FolderOpen fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {file.path}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Chip
                        icon={<AccessTime />}
                        label={file.lastModified ? formatDate(file.lastModified) : 'Unknown sync time'}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={formatFileSize(file.size)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {file.metadata?.sourceFileId && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Drive File ID: {file.metadata.sourceFileId}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
            </Box>
          )}
        </Box>
      )}
      {/* File Explorer */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Directory Structure
        </Typography>

        {bucketInfo.fileCount === 0 ? (
          <Alert severity="info">
            This bucket is empty. No files have been uploaded yet.
          </Alert>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, maxHeight: '600px', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                mb: 1
              }}
            >
              <Home color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight="medium">Root</Typography>
              <Typography variant="caption" color="text.secondary">
                ({bucketInfo.fileCount} files, {bucketInfo.totalSizeFormatted})
              </Typography>
            </Box>
            {bucketInfo.directoryStructure ? (
              <Box>
                {Object.entries(bucketInfo.directoryStructure).map(([name, data]) => (
                  <FileTreeItem
                    key={name}
                    name={name}
                    data={data}
                    path={[]}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No directory structure available
              </Typography>
            )}
          </Paper>
        )}
      </Box>
      {/* Metadata */}
      <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Bucket Metadata
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{formatDate(bucketInfo.created)}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Typography variant="caption" color="text.secondary">Updated</Typography>
            <Typography variant="body2">{formatDate(bucketInfo.updated)}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Typography variant="caption" color="text.secondary">Versioning</Typography>
            <Typography variant="body2">
              {bucketInfo.versioning?.enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Typography variant="caption" color="text.secondary">Lifecycle Rules</Typography>
            <Typography variant="body2">
              {bucketInfo.lifecycle?.rule?.length || 0} rules
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default GCSDetailView;